import axios from "axios";
import { useTranslation } from "i18next-vue";
import type {
  AxiosInstance,
  AxiosResponse,
  RawAxiosRequestConfig,
} from "axios";
import type { ConnectionSetup } from "@/api/connection/index";
import type { onRestError } from "@/types/rest";

export default class ConnectionRest {
  /**
   * API host.
   * @protected
   */
  protected _host: string;
  protected _config: ConnectionSetup;

  protected resourcesEndpoint: string;

  protected controller: AbortController;

  readonly axios: AxiosInstance;

  protected pingUrl = "/api/pub/version?ping=1";

  readonly requestTimeout = 30000;

  protected errorInterceptors: Map<string, onRestError> = new Map();

  constructor(config: ConnectionSetup) {
    this._config = { ...config };
    this._host = this._config.baseUrl;
    this.controller = new AbortController();
    this.axios = this.initAxios();
    this.resourcesEndpoint =
      this._config.baseUrl.replace(/\/$/, "") + "/api/resources";
  }

  protected getAxiosConfig(): RawAxiosRequestConfig {
    return {
      // mode: "no-cors",
      withCredentials: true,
    } as RawAxiosRequestConfig;
  }

  protected getMaxTimeout(): number {
    return import.meta.env.VITE_API_MAX_TIMEOUT || this.requestTimeout;
  }

  protected initAxios() {
    const inst = axios.create(this.getAxiosConfig());
    inst.defaults.baseURL = this._config.baseUrl;
    inst.interceptors.response.use(
      (resonse: AxiosResponse) => {
        return resonse;
      },
      (error: any) => {
        this.errorInterceptors.forEach((callback) => {
          callback(error);
        });
        return Promise.reject(error);
      }
    );
    return inst;
  }

  request(params: RawAxiosRequestConfig, timeout?: number) {
    // if (appStateStore().connected === false) {
    //   const { t } = useTranslation();
    //   throw new Error(t("error.ERR_NETWORK"));
    // }

    if (this.controller) {
      params.signal = this.controller.signal;
    }

    params.timeout = this.getMaxTimeout();

    if (timeout) {
      params.timeout = timeout;
    }

    if (import.meta.env.DEV) {
      const method = (params.method || "GET").toUpperCase();
      console.groupCollapsed(`[API:request] ${method} ${params.url}`);
      console.info(params);
      console.trace();
      console.groupEnd();
    }
    return this.axios.request(params);
  }

  requestNonCancellable(params: RawAxiosRequestConfig) {
    return this.axios.request(params);
  }

  async baseGet(params: RawAxiosRequestConfig, limit: number, page = 1) {
    const baseParams = {
      params: {},
      ...params,
    };
    baseParams.method = "get";
    baseParams.params.limit = limit;

    const reqParams = { ...baseParams };
    //reqParams.params.page = 1;
    //const head = await this.request(reqParams);
    //const total = parseInt(head.headers["pagination-count"] || "") || 0;
    //const pageCount = Math.ceil(total / limit);
    //let result = head.data;
    //reqParams = { ...baseParams };

    reqParams.params.page = page;
    if (reqParams.params.reload && reqParams.params.page > 1) {
      reqParams.params.reload = false;
    }
    const response = await this.request({
      ...reqParams,
    });

    const result = { data: response.data, headers: response.headers };
    return result;
  }

  async pagedGet(params: RawAxiosRequestConfig, limit: number) {
    const baseParams = {
      params: {},
      ...params,
    };
    baseParams.method = "get";
    baseParams.params.limit = limit;

    let reqParams = { ...baseParams };
    reqParams.params.page = 1;
    const head = await this.request(reqParams);
    const total = parseInt(head.headers["pagination-count"] || "") || 0;
    const pageCount = Math.ceil(total / limit);
    const result = head.data;

    if (pageCount > 1) {
      const requests = [];
      for (let i = 2; i <= pageCount; i++) {
        reqParams = { ...baseParams };
        reqParams.params.page = i;
        if (reqParams.params.reload && reqParams.params.page > 1) {
          reqParams.params.reload = false;
        }
        requests.push(
          this.request({
            ...reqParams,
          })
        );
      }
      const responses = await Promise.all(requests);
      responses.forEach((response) => {
        result.push(...response.data);
      });
    }
    return result;
  }

  resourceUrl(type: string, id: string): string {
    return this.resourcesEndpoint + "/" + type + "/" + id;
  }

  reset() {
    this.controller = new AbortController();
  }

  abort() {
    this.controller.abort();
    this.reset();
  }

  /**
   * Reachability probe for the ConnectionMonitor
   * (docs/tasks/connection-monitor-rewrite.md §5.3).
   *
   * Resolves on any HTTP response — a 401 still proves the device is
   * reachable — and rejects only on network error or timeout. Deliberately
   * bypasses request() so it carries no shared abort signal and triggers
   * no error interceptors' side effects beyond logging.
   */
  async probe(timeoutMs: number): Promise<void> {
    try {
      await this.axios.request({
        method: "get",
        url: this.pingUrl,
        timeout: timeoutMs,
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response) {
        return; // the device answered → reachable
      }
      throw e;
    }
  }

  addErrorInterceptor(name: string, callback: onRestError, replace = false) {
    if (this.errorInterceptors.has(name) && !replace) {
      throw new Error(`REST error handler with name "${name}" already exists`);
    }
    this.errorInterceptors.set(name, callback);
  }

  isCancelError(e: any): boolean {
    return axios.isAxiosError(e) && e.code === "ERR_NETWORK";
  }
  isUnauthorizedError(e: any): boolean {
    return axios.isAxiosError(e) && e.response?.status === 401;
  }
  isNotFoundError(e: any): boolean {
    return axios.isAxiosError(e) && e.response?.status === 404;
  }
  isConnectionError(e: any): boolean {
    return axios.isAxiosError(e);
  }
  isNetworkError(e: any): boolean {
    return axios.isAxiosError(e) && e.code === "ERR_NETWORK";
  }
  isUploadError(e: any): boolean {
    return axios.isAxiosError(e) && e.code === "ERR_BAD_REQUEST";
  }
}
